import { useState } from 'react'
import { Check, Pencil, X } from 'lucide-react'
import { clsx } from 'clsx'
import { useGameweekMatches, useSetMatchResult, type AdminMatch } from '../queries'
import styles from './MatchResultsTable.module.css'

type MatchResultsTableProps = {
  gameweekId:    string
  /** Editing is refused server-side once the round is finalized — reflect that here. */
  isFinalized:   boolean
}

export function MatchResultsTable({ gameweekId, isFinalized }: MatchResultsTableProps) {
  const { data: matches, isPending, isError, refetch } = useGameweekMatches(gameweekId)
  const [editingId, setEditingId] = useState<string | null>(null)

  if (isPending) return <div className={styles.empty}>Carregando partidas…</div>
  if (isError) {
    return (
      <div className={styles.empty}>
        Não foi possível carregar as partidas.{' '}
        <button type="button" className={styles.retry} onClick={() => void refetch()}>
          Tentar novamente
        </button>
      </div>
    )
  }
  if (matches.length === 0) {
    return <div className={styles.empty}>Nenhuma partida nesta rodada.</div>
  }

  const pending = matches.filter(
    (m) => !m.isThirdPlaceMatch && m.status !== 'FINISHED' && m.status !== 'POSTPONED',
  ).length

  return (
    <div className={styles.wrap}>
      <div className={styles.summary}>
        <span className={styles.summaryCount}>{matches.length}</span> partida(s) ·{' '}
        {pending === 0
          ? <span className={styles.summaryOk}>todas com resultado</span>
          : <span className={styles.summaryPending}>{pending} sem resultado</span>}
      </div>

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.thTime}>Horário</th>
              <th className={styles.thTeam}>Mandante</th>
              <th className={styles.thScore}>Placar</th>
              <th className={styles.thTeam}>Visitante</th>
              <th className={styles.thStatus}>Status</th>
              <th className={styles.thActions} aria-label="Ações" />
            </tr>
          </thead>
          <tbody>
            {matches.map((match) => (
              <MatchRow
                key={match.id}
                match={match}
                isEditing={editingId === match.id}
                canEdit={!isFinalized}
                onEdit={() => setEditingId(match.id)}
                onDone={() => setEditingId(null)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {isFinalized && (
        <p className={styles.note}>
          A rodada está finalizada — resete-a para poder corrigir um placar.
        </p>
      )}
    </div>
  )
}

type MatchRowProps = {
  match:     AdminMatch
  isEditing: boolean
  canEdit:   boolean
  onEdit:    () => void
  onDone:    () => void
}

function MatchRow({ match, isEditing, canEdit, onEdit, onDone }: MatchRowProps) {
  const [home, setHome] = useState(match.homeScore ?? 0)
  const [away, setAway] = useState(match.awayScore ?? 0)

  const setResult = useSetMatchResult()

  const homeName = match.homeTeam?.name ?? match.homeTeamPlaceholder ?? '—'
  const awayName = match.awayTeam?.name ?? match.awayTeamPlaceholder ?? '—'

  function handleSave() {
    setResult.mutate(
      { matchId: match.id, homeScore: home, awayScore: away },
      { onSuccess: () => onDone() },
    )
  }

  return (
    <tr className={clsx(match.isThirdPlaceMatch && styles.rowMuted)}>
      <td className={styles.tdTime}>{formatTime(match.kickoffAt)}</td>
      <td className={styles.tdTeam}>{homeName}</td>
      <td className={styles.tdScore}>
        {isEditing ? (
          <span className={styles.scoreEdit}>
            <input
              type="number"
              min={0}
              max={30}
              className={styles.scoreInput}
              value={home}
              onChange={(e) => setHome(Number(e.target.value))}
              disabled={setResult.isPending}
              aria-label={`Gols de ${homeName}`}
            />
            <span className={styles.scoreSep}>×</span>
            <input
              type="number"
              min={0}
              max={30}
              className={styles.scoreInput}
              value={away}
              onChange={(e) => setAway(Number(e.target.value))}
              disabled={setResult.isPending}
              aria-label={`Gols de ${awayName}`}
            />
          </span>
        ) : (
          <span className={styles.scoreView}>
            {match.homeScore ?? '–'} <span className={styles.scoreSep}>×</span> {match.awayScore ?? '–'}
          </span>
        )}
      </td>
      <td className={styles.tdTeam}>{awayName}</td>
      <td className={styles.tdStatus}>
        <span className={clsx(styles.statusBadge, styles[`status_${match.status}`])}>
          {STATUS_LABEL[match.status]}
        </span>
      </td>
      <td className={styles.tdActions}>
        {isEditing ? (
          <span className={styles.editActions}>
            <button
              type="button"
              className={styles.iconBtnPrimary}
              onClick={handleSave}
              disabled={setResult.isPending}
              aria-label="Salvar placar"
              title="Salvar placar"
            >
              {setResult.isPending
                ? <span className={styles.spinner} aria-hidden="true" />
                : <Check size={14} />}
            </button>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={onDone}
              disabled={setResult.isPending}
              aria-label="Cancelar"
              title="Cancelar"
            >
              <X size={14} />
            </button>
          </span>
        ) : (
          <button
            type="button"
            className={styles.iconBtn}
            onClick={onEdit}
            disabled={!canEdit}
            aria-label={`Editar placar de ${homeName} contra ${awayName}`}
            title={canEdit ? 'Lançar placar manualmente' : 'Rodada finalizada'}
          >
            <Pencil size={14} />
          </button>
        )}
      </td>
    </tr>
  )
}

const STATUS_LABEL: Record<AdminMatch['status'], string> = {
  SCHEDULED: 'A jogar',
  LIVE:      'Ao vivo',
  HALFTIME:  'Intervalo',
  FINISHED:  'Encerrada',
  POSTPONED: 'Adiada',
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day:    '2-digit',
    month:  '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
  })
}
